import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useEffect, useState } from "react"
import { Button, ButtonGroup, Form, Modal, Row, Table } from "react-bootstrap"
import Select from "react-select"
import { usePortAlerts } from "../hooks/usePortAlerts"
import { RiskLevelSelect } from "./RiskLevelSelect"
import { LocationSelection, UpdatePortAlertStruct } from "../hooks/portAlertsService"
import LocationPicker from "./LocationPicker"
import { formatLocation, formatLocationType } from "../helpers/portAlertHelpers"

interface PrevetPortAlertModalProps {
  show: boolean
  handleClose: () => void
}

interface SelectOption {
  label: string
  value: string
}

const PrevetPortAlertModal = ({
  show,
  handleClose,
}: PrevetPortAlertModalProps) => {
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[] | undefined>(
    undefined
  )
  const {
    portAlerts,
    organizations,
    createPortAlert,
    deletePortAlert,
    updatePortAlert,
  } = usePortAlerts({ org_ids: selectedOrgIds })

  useEffect(() => {
    if (organizations) setSelectedOrgIds(organizations?.map((i) => i.id))
  }, [organizations])

  const [selectedLocation, setSelectedLocation] = useState<LocationSelection | null>(null)
  const [selectedOrganization, setSelectedOrganization] =
    useState<SelectOption | null>(null)
  const [riskLevel, setRiskLevel] = useState<"red" | "green" | null>(null)

  const [organizationFilter, setOrganizationFilter] = useState<string>("")

  const handleAddPortAlerts = async () => {
    if (selectedLocation && selectedOrganization) {
      await createPortAlert({
        organizationId: selectedOrganization.value,
        countryUnlocode: selectedLocation.countryUnlocode || "",
        port: selectedLocation.port || "",
        terminal: selectedLocation.terminal || "",
        berth: selectedLocation.berth || "",
        riskLevel: riskLevel,
      })
    }
    resetForm()
  }

  const handleUpdatePortAlerts = async (portAlert: UpdatePortAlertStruct) => {
    if (portAlert) {
      await updatePortAlert(portAlert)
    }
  }

  const handleDelete = async (id: string) => {
    if (id) {
      await deletePortAlert(id)
    }
  }

  useEffect(() => {
    if (selectedLocation && selectedOrganization && portAlerts) {
      const exists = portAlerts.some(pa =>
        pa.organizationId === selectedOrganization.value
        && pa.countryUnlocode === selectedLocation.countryUnlocode
        && (pa.port || '') === (selectedLocation.port || '')
        && (pa.terminal || '') === (selectedLocation.terminal || '')
        && (pa.berth || '') === (selectedLocation.berth || '')
      )
      if (exists) setSelectedLocation(null)
    }
  }, [selectedOrganization, selectedLocation, portAlerts])

  const resetForm = () => {
    setSelectedLocation(null)
    setSelectedOrganization(null)
  }

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Port Alerts - Admin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Form.Label style={{ fontWeight: 700, fontSize: "17px" }}>
                Add port alerts
              </Form.Label>
            </Row>
            <Row className="mb-3">
              <div className="w-100 d-flex gap-2">
                <div className="w-100">
                  <Form.Group>
                    <Select
                      options={organizations?.map<SelectOption>(
                        (org) =>
                          ({
                            label: org.display_name,
                            value: org.id,
                          } as SelectOption)
                      )}
                      value={selectedOrganization}
                      onChange={setSelectedOrganization}
                      className="w-100"
                      placeholder="Select an organization"
                    />
                  </Form.Group>
                </div>
                <div className="w-100">
                  <Form.Group>
                    <LocationPicker
                      value={selectedLocation}
                      onChange={setSelectedLocation}
                      existingAlerts={selectedOrganization ? portAlerts?.filter(pa => pa.organizationId === selectedOrganization.value) || [] : []}
                    />
                  </Form.Group>
                </div>
                <div className="w-100">
                  <Form.Group>
                    <RiskLevelSelect
                      value={riskLevel}
                      onChange={setRiskLevel}
                    />
                  </Form.Group>
                </div>
                <div>
                  <Form.Group className="w-50">
                    <ButtonGroup>
                      <Button
                        variant="primary"
                        onClick={handleAddPortAlerts}
                        disabled={!selectedLocation || !selectedOrganization}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </ButtonGroup>
                  </Form.Group>
                </div>
              </div>
            </Row>
            <Row className="mb-3">
              <Form.Label style={{ fontWeight: 700, fontSize: "17px" }}>
                List of port alerts
              </Form.Label>
            </Row>
            <Row className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: "bold", fontSize: "14px" }}>
                  Filter by
                </Form.Label>
                <Form.Control
                  as="select"
                  value={organizationFilter}
                  onChange={(e) => setOrganizationFilter(e.target.value)}
                >
                  <option value="">All Organizations</option>
                  {organizations &&
                    organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.display_name}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              {!portAlerts || portAlerts.length === 0 ? (
                <p>No added port alerts.</p>
              ) : (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table striped bordered hover style={{ marginBottom: 0 }}>
                    <thead style={{ position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ fontWeight: "bold", fontSize: "14px" }}>
                          Organization
                        </th>
                        <th style={{ fontWeight: "bold", fontSize: "14px" }}>
                          Location
                        </th>
                        <th style={{ fontWeight: "bold", fontSize: "14px" }}>
                          Type
                        </th>
                        <th style={{ fontWeight: "bold", fontSize: "14px" }}>
                          Risk Level
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {portAlerts
                        .filter((pa) =>
                          organizationFilter
                            ? pa.organizationId === organizationFilter
                            : true
                        )
                        .map((pa) => (
                          <tr key={pa.id}>
                            <td>{pa.organization_name}</td>
                            <td>
                              <div>{formatLocation(pa)}</div>
                              {pa.berth && (
                                <small className="text-muted">
                                  {pa.country_name}{pa.port ? ` › ${pa.port}` : ''} › {pa.terminal}
                                </small>
                              )}
                              {!pa.berth && pa.terminal && (
                                <small className="text-muted">
                                  {pa.country_name}{pa.port ? ` › ${pa.port}` : ''}
                                </small>
                              )}
                              {!pa.berth && !pa.terminal && pa.port && (
                                <small className="text-muted">
                                  {pa.country_name}
                                </small>
                              )}
                            </td>
                            <td>{formatLocationType(pa)}</td>
                            <td>
                              <RiskLevelSelect
                                value={pa.riskLevel ?? null}
                                onChange={async (newRiskLevel) => {
                                  const updatedPortAlert: UpdatePortAlertStruct = {
                                    organizationId: pa.organizationId,
                                    countryUnlocode: pa.countryUnlocode,
                                    port: pa.port,
                                    terminal: pa.terminal,
                                    berth: pa.berth,
                                    description: pa.description || "",
                                    riskLevel: newRiskLevel,
                                    id: pa.id,
                                  }
                                  await handleUpdatePortAlerts(updatedPortAlert)
                                }}
                              />
                            </td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => pa.id && handleDelete(pa.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>{" "}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PrevetPortAlertModal
